/**
 * Portal_UI reactive store.
 *
 * A minimal, framework-free observer pattern that holds:
 *
 *   - the canonical `Case[]` for the current portfolio,
 *   - the derived `IndexSnapshot` and `TimelinePoint[]`,
 *   - parse warnings to surface in the Confirm_Modal,
 *   - the modal's own local UI state (open, in-flight extract, submit state, server error),
 *   - the last known load error (surfaced by the shell on boot failures).
 *
 * The store deliberately avoids React/Redux/immer: the reactive surface is
 * small enough that a plain `Set<Listener>` + shallow patches keeps the
 * fan-out obvious. Views wire themselves up via `subscribe` or the more
 * selective `subscribeSelector` and re-render when the slice they care
 * about changes.
 *
 * In non-production builds every emitted state object is `Object.freeze`d
 * as a runtime guard against accidental mutation from view code.
 */
import type {
  ApiErrorEnvelope,
  Case,
  DeckExtract,
  IndexSnapshot,
  ParseWarning,
  TimelinePoint,
} from "@aim/types";

/** Confirm_Modal local UI state. */
export interface ModalState {
  open: boolean;
  /** Populated after a successful `POST /api/parse`. */
  extract: DeckExtract | null;
  /** `true` while a `POST /api/cases` is in flight. */
  submitting: boolean;
  /** Last server-side error envelope, cleared on next successful submit. */
  serverError: ApiErrorEnvelope | null;
}

/** Root reactive state for the Portal_UI. */
export interface PortalState {
  cases: readonly Case[];
  index: IndexSnapshot | null;
  timeline: readonly TimelinePoint[];
  warnings: readonly ParseWarning[];
  modal: ModalState;
  /** Populated when boot (config load, initial fetch) fails; `null` otherwise. */
  loadError: string | null;
}

/** Selector function type; used by `select` and `subscribeSelector`. */
export type PortalStateSelector<T> = (s: PortalState) => T;

/** Store surface. */
export interface Store {
  /** Snapshot the current state. In non-production the returned object is frozen. */
  getState(): PortalState;

  /** Apply a shallow patch to the state (partial object form). */
  set(patch: Partial<PortalState>): void;
  /** Apply a shallow patch computed from the previous state (updater form). */
  set(fn: (prev: PortalState) => Partial<PortalState>): void;

  /** Compute a slice of the current state. */
  select<T>(selector: PortalStateSelector<T>): T;

  /**
   * Subscribe to every state emission. Returns an unsubscribe function that,
   * once called, stops further notifications.
   */
  subscribe(listener: (s: PortalState) => void): () => void;

  /**
   * Subscribe to a projected slice. The listener fires only when the slice
   * changes per `equalityFn` (default `Object.is`).
   */
  subscribeSelector<T>(
    selector: PortalStateSelector<T>,
    listener: (t: T, prev: T) => void,
    equalityFn?: (a: T, b: T) => boolean,
  ): () => void;
}

/** Fresh, empty portal state used when no `initial` is passed to `createStore`. */
export const defaultState: PortalState = Object.freeze({
  cases: Object.freeze([]) as readonly Case[],
  index: null,
  timeline: Object.freeze([]) as readonly TimelinePoint[],
  warnings: Object.freeze([]) as readonly ParseWarning[],
  modal: Object.freeze({
    open: false,
    extract: null,
    submitting: false,
    serverError: null,
  }) as ModalState,
  loadError: null,
}) as PortalState;

/**
 * Whether the current build is a production build. Environment lookup goes
 * through `globalThis.process` so this file compiles in the browser without
 * a `process` typing dependency.
 */
function isProduction(): boolean {
  const proc = (globalThis as { process?: { env?: { NODE_ENV?: string } } })
    .process;
  return proc?.env?.NODE_ENV === "production";
}

/** Deep-ish freeze for a single state emission — one level, plus `modal`. */
function freezeForEmit(state: PortalState): PortalState {
  Object.freeze(state.modal);
  return Object.freeze(state);
}

/**
 * Create a fresh reactive store, optionally seeded with a partial state.
 *
 * Overrides in `initial` are shallowly merged onto `defaultState`. The
 * returned store is independent of every other store instance.
 */
export function createStore(initial?: Partial<PortalState>): Store {
  // Start from a fresh, mutable copy so we don't hand out references to the
  // frozen module-level `defaultState` object.
  const { modal: initialModal, ...restInitial } = initial ?? {};
  let state: PortalState = {
    cases: defaultState.cases,
    index: defaultState.index,
    timeline: defaultState.timeline,
    warnings: defaultState.warnings,
    loadError: defaultState.loadError,
    ...restInitial,
    // If the caller provided a partial modal, merge it onto the default
    // rather than overwriting.
    modal: {
      ...defaultState.modal,
      ...(initialModal ?? {}),
    },
  };

  const listeners = new Set<(s: PortalState) => void>();

  const emit = (): void => {
    const snapshot = isProduction() ? state : freezeForEmit(state);
    // Copy the listener set so unsubscribes during emission don't skip peers.
    for (const listener of Array.from(listeners)) {
      listener(snapshot);
    }
  };

  const setImpl = (
    patchOrFn:
      | Partial<PortalState>
      | ((prev: PortalState) => Partial<PortalState>),
  ): void => {
    const patch =
      typeof patchOrFn === "function" ? patchOrFn(state) : patchOrFn;
    // Shallow merge onto the current state. `modal` gets shallow-merged one
    // level deeper as a convenience so callers can update `modal.open`
    // without rebuilding the entire modal object.
    const next: PortalState = { ...state, ...patch };
    if (patch.modal !== undefined) {
      next.modal = { ...state.modal, ...patch.modal };
    }
    state = next;
    emit();
  };

  // Combined implementation signature that satisfies both `set` overloads
  // on the `Store` interface. Cast at construction time so consumers see
  // the overloaded signature and this file's implementation stays narrow.
  const setPublic = ((
    patchOrFn:
      | Partial<PortalState>
      | ((prev: PortalState) => Partial<PortalState>),
  ): void => {
    setImpl(patchOrFn);
  }) as Store["set"];

  const store: Store = {
    getState(): PortalState {
      return isProduction() ? state : freezeForEmit(state);
    },

    set: setPublic,

    select<T>(selector: PortalStateSelector<T>): T {
      return selector(state);
    },

    subscribe(listener: (s: PortalState) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    subscribeSelector<T>(
      selector: PortalStateSelector<T>,
      listener: (t: T, prev: T) => void,
      equalityFn: (a: T, b: T) => boolean = Object.is,
    ): () => void {
      let previous = selector(state);
      const wrapped = (s: PortalState): void => {
        const current = selector(s);
        if (!equalityFn(current, previous)) {
          const prev = previous;
          previous = current;
          listener(current, prev);
        }
      };
      listeners.add(wrapped);
      return () => {
        listeners.delete(wrapped);
      };
    },
  };

  return store;
}
